import React, { useEffect, useRef } from 'react';
import T from 'i18n';
import { Button } from '@mui/material';
import { Spinner, TextForm, ToggleForm } from 'features/common';
import SelectFormAdapter from 'features/common/SelectFormAdapter';
import { useSelector } from 'react-redux';
import { selectCurrentCaseId } from 'common/selectors';
import { useUpdateUserSettings } from 'features/common/redux/updateUserSettings';
import { useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { useFetchCaseDetails } from '../redux/fetchCaseDetails';
import { Modal } from 'features/common/MaterialBasedComponents';
import { MAX_PROMPT_PLACEHOLDERS_ENTRIES } from './constants';

interface SearchOptionsProps {
  open: boolean;
  anchorEl: any;
  handleClose: any;
  isNewSearch?: boolean;
  directAnswersForChatEnabled?: boolean;
  /**
   * Controls which RAG version options are shown.
   * - 'search': existing SearchCenter versions (v1..v10 + latest)
   * - 'askAiSse': AskAiSSE chat versions (gen1..gen10 + latest)
   */
  versionOptionsVariant?: 'search' | 'askAiSse';
}

const SearchOptions = ({
  open,
  handleClose,
  isNewSearch,
  directAnswersForChatEnabled,
  versionOptionsVariant = 'search',
}: SearchOptionsProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm();
  const { caseDetails } = useFetchCaseDetails();
  const { userSettings, updateUserSettings, updateUserSettingsPending } = useUpdateUserSettings();
  const caseId = useSelector(selectCurrentCaseId);
  const { matches, indices, answers } = caseDetails.aiSettings || {};
  const indicesOptions = indices?.map((item: any) => ({
    label: item.name,
    value: item.name,
  }));
  const {
    multiQuery = matches.multiQuery,
    maxMarginalRelevance = matches.maxMarginalRelevance,
    numMatches = (userSettings.aiSettings && userSettings.aiSettings.numMatches) || 1000,
    directAnswers: rawDirectAnswers,
    version: savedVersion,
  } = userSettings.aiSettings || {};

  // Helper function to get party placeholder from array structure with fallback to old format
  const getPartyValue = (partyType: 'claimant' | 'respondent' | 'witness' | 'expert' | 'topic' | 'party') => {
    // First, try to get from new promptPlaceholders array
    const arrayData = userSettings?.aiSettings?.promptPlaceholders;
    if (Array.isArray(arrayData)) {
      const caseEntry = arrayData.find((entry: any) => entry.caseId === caseId);
      if (caseEntry?.[partyType]?.text) {
        return caseEntry[partyType].text;
      }
    }

    // Fallback to old format for backward compatibility (only for claimant/respondent)
    const rawValue =
      partyType === 'claimant' || partyType === 'respondent'
        ? userSettings.aiSettings?.[partyType]
        : undefined;
    if (typeof rawValue === 'string') {
      return rawValue;
    }
    if (rawValue?.caseId === caseId && rawValue?.text) {
      return rawValue.text;
    }

    return '';
  };

  // Extract text from placeholders using helper function
  const claimant = getPartyValue('claimant');
  const respondent = getPartyValue('respondent');
  const witness = getPartyValue('witness');
  const expert = getPartyValue('expert');
  const topic = getPartyValue('topic');
  const party = getPartyValue('party');

  const directAnswers =
    rawDirectAnswers !== undefined
      ? rawDirectAnswers
      : caseDetails?.settings?.dataRoom?.directAnswers !== undefined
        ? caseDetails.settings.dataRoom.directAnswers
        : false;

  const normalizeVersionForVariant = (raw: any) => {
    const v = typeof raw === 'string' ? raw.trim() : '';
    if (!v) return 'latest';
    const isGen = /^gen\d+$/i.test(v);
    const isV = /^v\d+$/i.test(v);
    if (versionOptionsVariant === 'search' && isGen) return 'latest';
    if (versionOptionsVariant === 'askAiSse' && isV) return 'latest';
    return v;
  };

  const version = normalizeVersionForVariant(savedVersion);

  const versionSelectOptions =
    versionOptionsVariant === 'askAiSse'
      ? [
          { label: 'latest', value: 'latest' },
          { label: 'gen2', value: 'gen2' },
          { label: 'gen1', value: 'gen1' },
        ]
      : [
          { label: 'latest', value: 'latest' },
          { label: 'v11', value: 'v11' },
          { label: 'v10', value: 'v10' },
          { label: 'v9', value: 'v9' },
          { label: 'v8', value: 'v8' },
          { label: 'v7', value: 'v7' },
          { label: 'v6', value: 'v6' },
          { label: 'v5', value: 'v5' },
          { label: 'v4', value: 'v4' },
          { label: 'v3', value: 'v3' },
          { label: 'v2', value: 'v2' },
          { label: 'v1', value: 'v1' },
        ];

  const defaultOptions = {
    multiQuery: matches.multiQuery,
    maxMarginalRelevance: matches.maxMarginalRelevance,
    numMatches: (userSettings.aiSettings && userSettings.aiSettings.numMatches) || 1000,
    index: indicesOptions[0].value,
    directAnswers: rawDirectAnswers === false ? false : true,
    version: 'latest',
    wrapMatchesWithContext: answers.wrapMatchesWithContext || false,
    useDefaultPrompt: answers.useDefaultPrompt || true,
    systemPrompt: answers.systemPrompt || '',
    sourcePrompt: answers.sourcePrompt || '',
    useChunkUpStrategy: answers.useChunkUpStrategy || false,
    chunkUpWhenLessThanMatches: answers.chunkUpWhenLessThanMatches || 10,
    chunkEitherSideOfMatch: answers.chunkEitherSideOfMatch || 1,
    queryRouter: answers.queryRouter || false,
    claimant: '',
    respondent: '',
    witness: '',
    expert: '',
    topic: '',
    party: '',
  };
  const [multiQueryValue, setMultiQueryValue] = React.useState<boolean>(multiQuery);
  const [maxMarginalRelevanceValue, setMaxMarginalRelevanceValue] =
    React.useState<boolean>(maxMarginalRelevance);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [resetClicked, setResetClicked] = React.useState<boolean>(false);
  const directAnswersWatch: boolean = watch('directAnswers', directAnswers);
  const versionSelectRef = useRef<HTMLDivElement>(null);

  const showVersionSelector =
    versionOptionsVariant === 'askAiSse'
      ? true
      : (isNewSearch && directAnswersWatch) || (!isNewSearch && directAnswersForChatEnabled);

  const showRetrievalOptions =
    versionOptionsVariant !== 'askAiSse' &&
    ((isNewSearch && !directAnswersWatch) || (!isNewSearch && !directAnswersForChatEnabled));

  const showPromptAndMultiComparison =
    versionOptionsVariant === 'askAiSse' ? true : directAnswersWatch || isNewSearch;

  useEffect(() => {
    if (!resetClicked) {
      reset((formValues) => ({
        ...formValues,
        multiQuery: multiQueryValue,
        maxMarginalRelevance: maxMarginalRelevanceValue,
      }));
      setLoading(false);
    }
  }, [multiQueryValue, maxMarginalRelevanceValue, reset, resetClicked]);

  useEffect(() => {
    if (directAnswersWatch && versionSelectRef.current) {
      versionSelectRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [directAnswersWatch]);

  return (
    <Modal
      show={open}
      title={T.translate('case.searchOptions')}
      handleClose={() => {
        handleClose();
      }}
      handleSave={handleSubmit((data, e) => {
        e?.preventDefault();
        e?.stopPropagation();

        // Get existing promptPlaceholders array or initialize empty array
        const existingArray = userSettings.aiSettings?.promptPlaceholders || [];

        // Find if there's an entry for the current caseId
        const caseIndex = existingArray.findIndex((entry: any) => entry.caseId === caseId);

        // Build the case entry
        const caseEntry: any = { caseId };
        if (data.claimant !== undefined) {
          caseEntry.claimant = { text: data.claimant };
        }
        if (data.respondent !== undefined) {
          caseEntry.respondent = { text: data.respondent };
        }
        if (data.witness !== undefined) {
          caseEntry.witness = { text: data.witness };
        }
        if (data.expert !== undefined) {
          caseEntry.expert = { text: data.expert };
        }
        if (data.topic !== undefined) {
          caseEntry.topic = { text: data.topic };
        }
        if (data.party !== undefined) {
          caseEntry.party = { text: data.party };
        }
        const hasPlaceholderValues =
          (data.claimant && data.claimant.trim()) ||
          (data.respondent && data.respondent.trim()) ||
          (data.witness && data.witness.trim()) ||
          (data.expert && data.expert.trim()) ||
          (data.topic && data.topic.trim()) ||
          (data.party && data.party.trim());

        // Create updated array
        let updatedArray;
        if (caseIndex >= 0) {
          // Update existing entry for this case
          updatedArray = [...existingArray];
          updatedArray[caseIndex] = {
            ...updatedArray[caseIndex],
            ...caseEntry,
          };
        } else if (hasPlaceholderValues) {
          // Add new entry for this case (only if at least one value is provided)
          // Check if we've reached the maximum limit
          if (existingArray.length >= MAX_PROMPT_PLACEHOLDERS_ENTRIES) {
            // Remove the oldest entry (first element) and add new entry at the end
            updatedArray = [...existingArray.slice(1), caseEntry];
          } else {
            // Simply add the new entry
            updatedArray = [...existingArray, caseEntry];
          }
        } else {
          // No changes to array
          updatedArray = existingArray;
        }

        // Prepare transformed data (exclude placeholders as they're stored in promptPlaceholders)
        const {
          claimant: _c,
          respondent: _r,
          witness: _w,
          expert: _e,
          topic: _t,
          party: _p,
          ...restData
        } = data;

        updateUserSettings({
          userSettings: {
            ...userSettings,
            aiSettings: {
              ...userSettings.aiSettings,
              ...restData,
              promptPlaceholders: updatedArray,
            },
          },
        });
        handleClose();
      })}
      disableSave={updateUserSettingsPending}
      saveTitle={T.translate('generic.save')}
      buttons={
        isNewSearch ? (
          <>
            <Button
              variant="contained"
              type="button"
              color="primary"
              disabled={updateUserSettingsPending}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setLoading(true);
                setResetClicked(true);
                setMaxMarginalRelevanceValue(false);
                setMultiQueryValue(false);
                reset(defaultOptions);
                updateUserSettings({
                  userSettings: {
                    ...userSettings,
                    aiSettings: {
                      ...userSettings.aiSettings,
                      ...defaultOptions,
                    },
                  },
                }).then(() => {
                  setLoading(false);
                  setResetClicked(false);
                });
              }}
            >
              {T.translate('generic.reset')}
            </Button>
          </>
        ) : undefined
      }
    >
      <div style={{ minWidth: '25rem', minHeight: '15rem', paddingBottom: '1rem' }}>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Form.Group>
            {loading ? (
              <div style={{ marginBottom: '1rem' }}>
                <Spinner />
              </div>
            ) : (
              <>
                {isNewSearch && (
                  <div style={{ marginBottom: '2rem', width: '50%' }}>
                    <ToggleForm
                      {...register('directAnswers')}
                      /*@ts-expect-error JS ToggleForm lacks TS props typing */
                      defaultValue={directAnswers}
                      label={T.translate('case.directAnswers')}
                    />
                  </div>
                )}
                {showRetrievalOptions && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'row', marginBottom: '2rem' }}>
                      <div style={{ width: '50%' }}>
                        <TextForm
                          type="number"
                          {...register('numMatches', { required: true })}
                          /*@ts-expect-error JS TextForm lacks TS props typing */
                          label={T.translate('case.maximumNumberOfMatches')}
                          placeholder={T.translate('case.maximumNumberOfMatchesPlaceholder')}
                          defaultValue={numMatches}
                          errors={errors}
                          autofocus
                        />
                      </div>
                    </div>
                  </>
                )}
                {showVersionSelector && (
                  <div
                    ref={versionSelectRef}
                    style={{ display: 'flex', flexDirection: 'row', marginBottom: '2rem' }}
                  >
                    <div style={{ width: '50%' }}>
                      <SelectFormAdapter
                        control={control}
                        name={'version'}
                        label={T.translate('generic.agentVersion')}
                        options={versionSelectOptions}
                        defaultValue={version}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>
                  </div>
                )}
                {showPromptAndMultiComparison && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'row', marginBottom: '1rem' }}>
                      <div style={{ width: '50%' }}>
                        <TextForm
                          {...register('claimant')}
                          /*@ts-expect-error JS TextForm lacks TS props typing */
                          label={T.translate('generic.claimant')}
                          placeholder={T.translate('generic.claimant')}
                          defaultValue={claimant}
                          errors={errors}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', marginBottom: '1rem' }}>
                      <div style={{ width: '50%' }}>
                        <TextForm
                          {...register('respondent')}
                          /*@ts-expect-error JS TextForm lacks TS props typing */
                          label={T.translate('generic.respondent')}
                          placeholder={T.translate('generic.respondent')}
                          defaultValue={respondent}
                          errors={errors}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', marginBottom: '1rem' }}>
                      <div style={{ width: '50%' }}>
                        <TextForm
                          {...register('witness')}
                          /*@ts-expect-error JS TextForm lacks TS props typing */
                          label={T.translate('generic.witness')}
                          placeholder={T.translate('generic.witness')}
                          defaultValue={witness}
                          errors={errors}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', marginBottom: '2rem' }}>
                      <div style={{ width: '50%' }}>
                        <TextForm
                          {...register('expert')}
                          /*@ts-expect-error JS TextForm lacks TS props typing */
                          label={T.translate('generic.expert')}
                          placeholder={T.translate('generic.expert')}
                          defaultValue={expert}
                          errors={errors}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', marginBottom: '2rem' }}>
                      <div style={{ width: '50%' }}>
                        <TextForm
                          {...register('topic')}
                          /*@ts-expect-error JS TextForm lacks TS props typing */
                          label={T.translate('generic.topic')}
                          placeholder={T.translate('generic.topic')}
                          defaultValue={topic}
                          errors={errors}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', marginBottom: '2rem' }}>
                      <div style={{ width: '50%' }}>
                        <TextForm
                          {...register('party')}
                          /*@ts-expect-error JS TextForm lacks TS props typing */
                          label={T.translate('generic.party')}
                          placeholder={T.translate('generic.party')}
                          defaultValue={party}
                          errors={errors}
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </Form.Group>
        </Form>
      </div>
    </Modal>
  );
};

export default SearchOptions;
